import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TypingTestModal from './components/TypingTestModal';
import { getTypingTestsForTrainee } from '../../api_service';

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: 'text-warning',  bg: 'bg-warning/10',  icon: 'Clock'       },
  COMPLETED: { label: 'Completed', color: 'text-success',  bg: 'bg-success/10',  icon: 'CheckCircle' },
};

const MyTyping = () => {
  const navigate = useNavigate();
  const traineeId = sessionStorage.getItem('userId');
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testTarget, setTestTarget] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadTests(); }, []);

  const loadTests = async () => {
    setLoading(true);
    try {
      const res = await getTypingTestsForTrainee(traineeId);
      setTests(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error bg-error/10' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning bg-warning/10' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning bg-warning/10' };
    return { text: `${diff} days left`, cls: 'text-muted-foreground bg-muted/40' };
  };

  const filtered = tests.filter(item => {
    if (filter === 'all') return true;
    return (item.result?.status || 'PENDING') === filter;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="trainee"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Typing Tests</h1>
          <p className="text-sm text-muted-foreground mt-1">Take your assigned typing speed tests</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { key: 'all',       label: 'Total',     icon: 'Keyboard',     color: 'text-primary' },
            { key: 'PENDING',   label: 'Pending',   icon: 'Clock',        color: 'text-warning' },
            { key: 'COMPLETED', label: 'Completed', icon: 'CheckCircle',  color: 'text-success' },
          ].map(s => {
            const count = s.key === 'all' ? tests.length
              : tests.filter(t => (t.result?.status || 'PENDING') === s.key).length;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`bg-card border rounded-xl p-4 text-left transition-all ${filter === s.key ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/40'}`}
              >
                <Icon name={s.icon} size={20} className={`${s.color} mb-2`} />
                <p className="text-2xl font-bold text-foreground">{count}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </button>
            );
          })}
        </div>

        {/* Test list */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading tests...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Icon name="Keyboard" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No typing tests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(item => {
              const status = item.result?.status || 'PENDING';
              const cfg = STATUS_CONFIG[status];
              const dl = deadlineLabel(item.test?.deadline);
              return (
                <div key={item.test?.testId} className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-foreground">{item.test?.title}</h3>
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {dl && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${dl.cls}`}>{dl.text}</span>
                        )}
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Duration: {item.test?.durationSeconds}s</span>
                        <span>Deadline: {item.test?.deadline || 'None'}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {status === 'PENDING' && (
                        <Button size="sm" onClick={() => setTestTarget(item)} iconName="Play" iconSize={14}>
                          Start Test
                        </Button>
                      )}
                      {status === 'COMPLETED' && (
                        <div className="text-right">
                          <p className="text-2xl font-bold text-success">{item.result?.wpm} <span className="text-sm font-normal">WPM</span></p>
                          <p className="text-xs text-muted-foreground">{item.result?.accuracy}% accuracy</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {testTarget && (
        <TypingTestModal
          item={testTarget}
          traineeId={traineeId}
          onClose={() => setTestTarget(null)}
          onCompleted={loadTests}
        />
      )}
    </div>
  );
};

export default MyTyping;
